import { Calendar, Music2, Pause, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import apiClient from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { scheduleApi } from '../../api/schedule';
import { format } from 'date-fns';

type ScheduleItem = {
  id: string;
  startTime: string;
  endTime: string;
  status: string;
  isActive: boolean;
  show: any;
};

const playlist = [
  { 
    title: 'Midnight Jazz Sessions', 
    host: 'Sarah Jenkins', 
    type: 'Music',
    image: 'https://images.unsplash.com/photo-1493225255756-d9584f8606e9?q=80&w=1000&auto=format&fit=crop'
  },
  { 
    title: 'Future Tech Today', 
    host: 'Marcus Brown', 
    type: 'Podcast',
    image: "https://images.unsplash.com/photo-1538388149542-5e24932d11a8?q=80&w=1074&auto=format&fit=crop"
  },
  { 
    title: 'The Daily Conversation', 
    host: 'Alex & Team', 
    type: 'Talk Show',
    image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=1000&auto=format&fit=crop'
  }
];

function formatToHHMM(isoString: string): string {
  const date = new Date(isoString);

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours} : ${minutes}`;
}

export default function Dashboard() {
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(45);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [upcomingSchedules, setUpcomingSchedules] = useState<any | []>([])
  const [historySchedules, setHistorySchedules] = useState<any | []>([])

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying2, setIsPlaying2] = useState(false);

  const [scheduleLoading, setScheduleLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(true)

  const { landingDataLoading, landingPageData, setLandingPageData, setLandingDataLoading, siteData: data } = useAuth()
  const [liveSchedule, setLiveSchedule] = useState<any>(null)

  const getUpcomingSchedules=async()=>{
    setScheduleLoading(true)
    try{
      const {data} = await apiClient.get(`/schedules`, {params: {status: "upcoming", limit: 4, sortField: "createdAt", sortOrder: "desc"}})

      if(data?.success){
        setUpcomingSchedules(data?.data)
      }
    }
    catch(error: any){
      toast.error(error?.response?.data?.message || error?.message)
    }
    finally{
      setScheduleLoading(false)
    }
  }

  const getHisorySchedules=async()=>{
    setHistoryLoading(true)
    try{
      const {data} = await apiClient.get(`/schedules`, {params: {status: "completed", limit: 4, sortField: "createdAt", sortOrder: "desc"}})

      if(data?.success){
        setHistorySchedules(data?.data)
      }
    }
    catch(error: any){
      toast.error(error?.response?.data?.message || error?.message)
    }
    finally{
      setHistoryLoading(false)
    }
  }

  function getSingleLiveSchedule(
    scheduleItems: ScheduleItem[]
  ): ScheduleItem | null {
    const liveSchedules = scheduleItems
      .filter(
        (item) => item.isActive && item.status === 'live'
      )
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() -
          new Date(b.startTime).getTime()
      );

    return liveSchedules.length > 0 ? liveSchedules[0] : null;
  }

  useEffect(()=>{
    if(landingPageData){
      const data = getSingleLiveSchedule(landingPageData?.scheduleItems)
      setLiveSchedule(data)
    }
  },[landingPageData])

  const fetchData = async () => {
    try {
      setLandingDataLoading(true)
      const today = format(new Date(), "yyyy-MM-dd");
      const response = await scheduleApi.getLandingPageData(today);
      // console.log("response", response.data)
      if (response.success) {
        const data = {
          scheduleItems: response.data.scheduleItems
            .filter((schedule) => {
              const scheduleDate = schedule.date.split("T")[0];
              return scheduleDate === today && schedule.isActive;
            })
            .map((schedule) => ({
              ...schedule,
              iconPath: "/path-to-icon",
              gradient: "from-[#174868] to-[#147b89]",
            })),
          featuredShows: response.data.featuredShows,
          historyItems: response.data.historyItems,
        }
        setLandingPageData(data);
      } else {
        throw new Error("Failed to fetch data");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLandingDataLoading(false);
    }
  };

  useEffect(()=>{
    getUpcomingSchedules();
    getHisorySchedules();
    fetchData();

    if(currentIndex && progress){
      console.log("")
    }
  },[])

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying2) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }

    setIsPlaying2(!isPlaying2);
  };

  // Simulate progress bar & content loop
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          // Next track in loop
          setCurrentIndex(prev => (prev + 1) % playlist.length);
          return 0;
        }
        return p + 0.5; // Slower progress
      });
    }, 100);
    return () => clearInterval(interval);
  }, [isPlaying]);

  // const handleNext = () => {
  //   setCurrentIndex((prev) => (prev + 1) % playlist.length);
  //   setProgress(0);
  // };

  // const handlePrev = () => {
  //   setCurrentIndex((prev) => (prev - 1 + playlist.length) % playlist.length);
  //   setProgress(0);
  // };

  // const currentTrack = playlist[currentIndex];

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Dashboard</h1>
          <p className="text-slate-400 mt-2">Live station overview and controls.</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-full text-green-400 text-xs font-medium animate-pulse">
          <div className="w-2 h-2 bg-green-500 rounded-full" />
          ON AIR
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <audio
        ref={audioRef}
        src={ data ? data?.radioLink : `https://globalwebmusic.com/akaram.mp3`}
        preload="none"
      />
        {/* Widget 1: Now Playing (Live Player View) */}
        <div className="lg:col-span-2 bg-[#0B1623] border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden group">
          {/* Background Blur Effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 to-purple-900/20 z-0" />
          
          <div className="relative z-10 flex flex-col sm:flex-row gap-8 items-center">
            {/* Album Art */}
            {/* <div className="w-48 h-48 rounded-2xl bg-black/40 shadow-2xl relative overflow-hidden border border-white/10 shrink-0">
              <img 
                src={currentTrack.image}
                alt="Album Art" 
                className="w-full h-full object-cover transition-opacity duration-500" 
                key={currentTrack.image} // Force re-render animation
              />
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                  <Activity className="w-6 h-6 text-white" />
                </div>
              </div>
            </div> */}

            {/* Track Info & Controls */}
            <div className="flex-1 w-full text-center sm:text-left">
              <div className="mb-6">
                {/* <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                  <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold tracking-wide">
                    NOW PLAYING
                  </span>
                  <span className="inline-block px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-bold tracking-wide uppercase">
                    {currentTrack.type}
                  </span>
                </div> */}

                {
                  landingDataLoading ?  <div className="h-5 w-48 bg-white/20 rounded animate-pulse mb-2" /> : 
                  <h2 className="text-3xl font-bold text-white mb-1">{liveSchedule ? liveSchedule?.show?.name : "The Morning Show"}</h2>
                }
                {
                  landingDataLoading ? <div className="h-4 w-32 bg-white/15 rounded animate-pulse" /> : 
                  <p className="text-slate-400 text-lg">Host: {liveSchedule ? liveSchedule?.show?.hostName : "Sarah Johnson"}</p>
                }
                {/* <h2 className="text-3xl font-bold text-white mb-1">{currentTrack.title}</h2>
                <p className="text-slate-400 text-lg">Host: {currentTrack.host}</p> */}
              </div>

              {/* Progress Bar */}
              {/* <div className="space-y-2 mb-6">
                <div className="flex justify-between text-xs text-slate-500 font-medium">
                  <span>{Math.floor((progress / 100) * 60)}:{Math.floor(((progress / 100) * 60) % 60).toString().padStart(2, '0')}</span>
                  <span>60:00</span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-200 ease-linear relative"
                    style={{ width: `${progress}%` }}
                  >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full shadow-lg" />
                  </div>
                </div>
              </div> */}


              {/* Controls */}
              <div className="flex items-center justify-center sm:justify-start gap-6">
                {/* <button onClick={handlePrev} className="text-slate-400 hover:text-white transition-colors">
                  <SkipBack className="w-6 h-6" />
                </button> */}
                <button 
                  onClick={() => {setIsPlaying(!isPlaying); togglePlay()}}
                  className="w-14 h-14 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition-transform shadow-lg shadow-white/10"
                >
                  {isPlaying2 ? <Pause className="w-6 h-6 fill-current" /> : <Play className="w-6 h-6 fill-current ml-1" />}
                </button>
                {/* <button onClick={handleNext} className="text-slate-400 hover:text-white transition-colors">
                  <SkipForward className="w-6 h-6" />
                </button> */}
              </div>
            </div>
          </div>
        </div>

        {/* Widget 2: Stats Summary */}
        {/* <div className="space-y-6">
           <div className="bg-[#0B1623] border border-white/10 rounded-2xl p-6 flex items-center justify-between">
             <div>
               <p className="text-slate-400 text-sm font-medium mb-1">Current Listeners</p>
               <p className="text-3xl font-bold text-white">1,248</p>
             </div>
             <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-400">
               <Users className="w-6 h-6" />
             </div>
           </div>
           
           <div className="bg-[#0B1623] border border-white/10 rounded-2xl p-6 flex items-center justify-between">
             <div>
               <p className="text-slate-400 text-sm font-medium mb-1">Stream Quality</p>
               <p className="text-3xl font-bold text-white">320 <span className="text-sm text-slate-500 font-normal">kbps</span></p>
             </div>
             <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center text-green-400">
               <Activity className="w-6 h-6" />
             </div>
           </div>

           <div className="bg-[#0B1623] border border-white/10 rounded-2xl p-6 flex items-center justify-between">
             <div>
               <p className="text-slate-400 text-sm font-medium mb-1">Uptime</p>
               <p className="text-3xl font-bold text-white">24<span className="text-lg">:</span>12<span className="text-lg">:</span>45</p>
             </div>
             <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-400">
               <Clock className="w-6 h-6" />
             </div>
           </div>
        </div> */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Widget 3: Upcoming Schedule */}
        {
          scheduleLoading ? <DashboardSkeleton /> :
          <div className="bg-[#0B1623] border border-white/10 rounded-3xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-cyan-400" />
                Upcoming Schedule
              </h3>
              <Link to="/admin/schedule?page=1&limit=10" className="text-sm text-cyan-400 hover:text-cyan-300 font-medium">View Full</Link>
            </div>
            
            <div className="space-y-4">
              {
              upcomingSchedules?.map((item: any, i: number) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors group">
                  <div className="w-16 text-center shrink-0">
                    <p className="text-lg font-bold text-white">{formatToHHMM(item?.startTime)}</p>
                  </div>
                  <div className="w-px h-8 bg-white/10" />
                  <div className="flex-1">
                    {/* <h4 className="font-bold text-white group-hover:text-cyan-400 transition-colors">{item?.show?.name}</h4> */}
                    <h4 className="font-bold text-white group-hover:text-cyan-400 transition-colors">
                      {item?.show?.name && item.show.name.length > 25
                        ? `${item.show.name.slice(0, 25)}...`
                        : item?.show?.name}
                    </h4>
                    <p className="text-sm text-slate-400">{item?.show?.hostName}</p>
                  </div>

                  <div className='flex gap-1'>
                    {item?.show?.categories?.slice(0,2)?.map((e: any, i: number)=>{
                      return (
                        <span key={i} className="px-3 py-1 rounded-lg bg-black/20 text-xs font-medium text-slate-300 border border-white/5">
                          {e?.name}
                        </span>
                      )
                    })}
                    {item?.show?.categories?.length > 2 && (
                      <span className="px-3 py-1 rounded-lg bg-black/10 text-xs text-slate-400 border border-white/5">
                        +{item.show.categories.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
              ))}

              { (!scheduleLoading) && upcomingSchedules?.length < 1 && <div className='h-full'> <EmptyState message='No upcoming schedules found' /> </div> }
            </div>
          </div>
        }

        {/* Widget 4: Playback History */}
        {
          historyLoading ? <DashboardSkeleton /> :
          <div className="bg-[#0B1623] border border-white/10 rounded-3xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Music2 className="w-5 h-5 text-purple-400" />
                Playback History
              </h3>
              <Link to="/admin/schedule?page=1&limit=10&status=completed" className="text-sm text-purple-400 hover:text-purple-300 font-medium">View All</Link>
            </div>

            <div className="space-y-1">
              {
              // [
              //   { title: 'Bohemian Rhapsody', artist: 'Queen', time: '14:15' },
              //   { title: 'Hotel California', artist: 'Eagles', time: '14:08' },
              //   { title: 'Imagine', artist: 'John Lennon', time: '14:04' },
              //   { title: 'Sweet Child O\' Mine', artist: 'Guns N\' Roses', time: '13:58' },
              //   { title: 'Billie Jean', artist: 'Michael Jackson', time: '13:54' },
              // ]
              historySchedules?.map((item: any, i: number) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-4">
                    <p className="text-base text-slate-500 font-mono w-16">{formatToHHMM(item?.startTime)}</p>
                    <div>
                      <p className="text-sm font-medium text-white">
                        {item?.show?.name && item.show.name.length > 30
                          ? `${item.show.name.slice(0, 30)}...`
                          : item?.show?.name}
                      </p>
                      <p className="text-xs text-slate-400">{item?.show?.hostName}</p>
                    </div>
                  </div>
                  {/* <button className="p-2 text-slate-500 hover:text-white transition-colors">
                    <Play className="w-3 h-3" />
                  </button> */}
                  <div className='flex gap-2'>
                    {item?.show?.categories?.slice(0,1)?.map((e: any, i: number)=>{
                      return (
                        <span key={i} className="px-3 py-1 rounded-lg bg-black/20 text-xs font-medium text-slate-300 border border-white/5">
                          {e?.name}
                        </span>
                      )
                    })}
                    {item?.show?.categories?.length > 1 && (
                      <span className="px-3 py-1 rounded-lg bg-black/10 text-xs text-slate-400 border border-white/5">
                        +{item.show.categories.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
              ))}

              { (!historyLoading) && historySchedules?.length < 1 && <div className='h-full'> <EmptyState message='No playback history found' /> </div> }
            </div>
          </div>
        }

        {/* <EmptyState message='No upcoming schedules found' />
        <EmptyState message='No playback history found' /> */}
      </div>
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-black/40 p-6">
      {/* moving highlight */}
      <div className="pointer-events-none absolute inset-0 animate-ping bg-white/10 opacity-20" />

      {/* content */}
      <div className="relative space-y-4">
        <div className="flex justify-between mb-6">
          <div className="h-5 w-52 rounded bg-white/20 animate-pulse" />
          <div className="h-4 w-16 rounded bg-white/20 animate-pulse" />
        </div>

        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="h-14 w-full rounded-xl bg-white/20 animate-pulse"
          />
        ))}
      </div>
    </div>
  );
}

export function EmptyState({ message }: {message?: string}) {
  if (!message) return null

  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-10 text-center h-full">
      <p className="text-sm text-slate-400">{message}</p>
    </div>
  )
}
